import { MEDIA_RESOURCE_TYPE } from "../CreatePageConstants";
import Sideways from "./elements/particles/Sideways";
import StarTravel from "./elements/particles/StarTravel";
import Flock from "./elements/particles/Flock";
import { FlatWave } from "./elements/visualizers/FlatWave";
import ReactiveImage from "./elements/media/ReactiveImage";
import ReactiveVideo from "./elements/media/ReactiveVideo";
import ReactiveColor from "./elements/media/ReactiveColor";
import { WaveCircle } from "./elements/visualizers/WaveCircle";
import { CONSTANTS, CORNER_POSITION, MOVEMENT_DIRECTION, LAYOUT_TYPE, PARTICLE_DIRECTIONS, PREVIEW_RESOLUTION, REFLECTION_TYPE, VISUALIZER_SHAPE, WAVE_STYLE, WAVE_TYPE, YOUTUBE_CTA_CONFIG } from "./PreviewDisplayConstants";
import AnimatedElement from "./elements/media/AnimatedElement";
import ImageElementsContainer from "./elements/media/ImageElementsContainer";
import TextContainer from "./elements/text/TextContainer";
import LyricsContainer from "./elements/text/LyricsContainer";

export const isColorOrGradient = (mediaSourceOrType) => {
    const mediaResourceType = mediaSourceOrType?.mediaResourceType ?? mediaSourceOrType;
    return mediaResourceType === MEDIA_RESOURCE_TYPE.COLOR || mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT;
};

export function chooseBackgroundRenderer(mediaSource, stage) {
    if (mediaSource && mediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO)
        return new ReactiveVideo(stage, 1);

    if (isColorOrGradient(mediaSource))
        return new ReactiveColor(stage, 1);

    return new ReactiveImage(stage, 1);
};

export function chooseParticlesRenderer(direction, stage) {
    if (direction === PARTICLE_DIRECTIONS.OUT)
        return new StarTravel(stage, 2);

    if (direction === PARTICLE_DIRECTIONS.FLOCK)
        return new Flock(stage, 2);

    return new Sideways(stage, 2);
};

export function chooseVisualizerRenderer(shape, stage) {
    if (shape === VISUALIZER_SHAPE.CIRCLE)
        return new WaveCircle(stage, 3);

    return new FlatWave(stage, 3);
};

export function chooseYouTubeCtaRenderer(youTubeCtaSettings, stage, onResourcesLoaded) {
    return new AnimatedElement(
        stage,
        generateYouTubeCtaFrames(),
        getYouTubeCtaPosition(
            youTubeCtaSettings.cornerPosition,
            CONSTANTS.DEFAULT_WIDTH,
            CONSTANTS.DEFAULT_HEIGHT
        ),
        getYouTubeCtaSize(
            CONSTANTS.DEFAULT_WIDTH,
            CONSTANTS.DEFAULT_HEIGHT
        ),
        YOUTUBE_CTA_CONFIG.SPEED,
        youTubeCtaSettings.enabled,
        YOUTUBE_CTA_CONFIG.STATIC_FRAME_NUMBER,
        7,
        onResourcesLoaded
    );
};

export function chooseTextRenderer(stage) {
    return new TextContainer(stage, 4);
};

export function chooseLyricsRenderer(stage) {
    return new LyricsContainer(stage, 6);
};

// The image elements sit above the static text, the visualizer and the particles, and below the
// lyrics and the subscribe animation, so a picture never covers a line of lyrics or the call to
// action. The export renderer keeps the same order in SpecterrRenderer/visualization/constants.js.
export function chooseImageElementsRenderer(stage) {
    return new ImageElementsContainer(stage, 5);
};

const getSpectrumOptionsAndBaseWaveScale = (waveType, shape, reflectionType, computedBarCount, smoothed) => {
    switch (waveType) {
        case WAVE_TYPE.BASS_SPECTRUM:
            return {
                primarySpectrumOptions: {
                    smoothingPoints: CONSTANTS.BASS_SMOOTHING_POINTS,
                    smoothingPasses: 4,
                    maxShiftPasses: 0,
                    barCount: computedBarCount,
                    smoothed: smoothed,
                    loop: shape === VISUALIZER_SHAPE.CIRCLE && reflectionType !== REFLECTION_TYPE.VERTICAL
                },
                baseWaveScale: 2
            };

        case WAVE_TYPE.WIDE_SPECTRUM:
            return {
                primarySpectrumOptions: {
                    smoothingPoints: CONSTANTS.WIDE_SMOOTHING_POINTS,
                    smoothingPasses: 4,
                    maxShiftPasses: 0,
                    barCount: computedBarCount,
                    smoothed: smoothed,
                    loop: shape === VISUALIZER_SHAPE.CIRCLE && reflectionType !== REFLECTION_TYPE.VERTICAL
                },
                baseWaveScale: 0.333
            };

        default:
            return { primarySpectrumOptions: {}, baseWaveScale: 1 };
    }
};

const createAvailableOptions = (baseWaveScale, waveScale, primarySpectrumOptions) => {
    const createOption = () => ({
        scale: 1,
        waveScale: baseWaveScale * waveScale,
        spectrumOptions: { ...primarySpectrumOptions },
        frameDelay: 0,
        heightAdjust: 0,
    });

    return Array.from({ length: 7 }, createOption);
}

const applyCustomSettings = (customSettings, waveCircleOptions, shape) => {
    customSettings?.forEach((settings, index) => {
        if (settings && settings.enabled) {
            const { primarySpectrumOptions, baseWaveScale } = getSpectrumOptionsAndBaseWaveScale(
                settings.waveType,
                shape,
                settings.reflectionType,
                settings.barCount,
                settings.smoothed
            );

            waveCircleOptions[index].waveScale = baseWaveScale * settings.waveScaleFactor;
            waveCircleOptions[index].spectrumOptions = primarySpectrumOptions;
        }
    });
}

const configureAvailableOptions = (availableOptions, layoutType, separation, direction) => {
    const inward = direction === MOVEMENT_DIRECTION.INWARD;

    if (layoutType === LAYOUT_TYPE.WEBBED) {
        availableOptions[0].spectrumOptions.smoothingPasses = 4;
        availableOptions[0].spectrumOptions.maxShiftPasses = 0;
        availableOptions[1].spectrumOptions.smoothingPasses += Math.floor(8 * separation);
        availableOptions[1].spectrumOptions.maxShiftPasses += Math.floor(4 * separation);
        availableOptions[2].spectrumOptions.smoothingPasses += Math.floor(12 * separation);
        availableOptions[2].spectrumOptions.maxShiftPasses += Math.floor(8 * separation);
        availableOptions[3].spectrumOptions.smoothingPasses += Math.floor(16 * separation);
        availableOptions[3].spectrumOptions.maxShiftPasses += Math.floor(12 * separation);
        availableOptions[4].spectrumOptions.smoothingPasses += Math.floor(20 * separation);
        availableOptions[4].spectrumOptions.maxShiftPasses += Math.floor(16 * separation);
        availableOptions[5].spectrumOptions.smoothingPasses += Math.floor(24 * separation);
        availableOptions[5].spectrumOptions.maxShiftPasses += Math.floor(20 * separation);
        availableOptions[6].spectrumOptions.smoothingPasses += Math.floor(28 * separation);
        availableOptions[6].spectrumOptions.maxShiftPasses += Math.floor(24 * separation);

        // WEBBED normally separates layers ONLY by smoothing (same size), which collapses for an
        // inward direction (every layer nests to the same radius and the front one hides the rest).
        // Add the same amplitude ladder as STACKED so inward WEBBED stays visibly separated. Outward
        // WEBBED is left unchanged to preserve its original look.
        if (inward) {
            availableOptions[0].waveScale *= 0.25;
            availableOptions[1].waveScale *= 0.25 + 0.25 * separation;
            availableOptions[2].waveScale *= 0.25 + 0.50 * separation;
            availableOptions[3].waveScale *= 0.25 + 0.75 * separation;
            availableOptions[4].waveScale *= 0.25 + 1.0 * separation;
            availableOptions[5].waveScale *= 0.25 + 1.25 * separation;
            availableOptions[6].waveScale *= 0.25 + 1.50 * separation;
        }
    } else if (layoutType === LAYOUT_TYPE.LAYERED) {
        availableOptions[0].spectrumOptions.smoothingPasses = 4;
        availableOptions[0].spectrumOptions.maxShiftPasses = 0;
        availableOptions[1].spectrumOptions.smoothingPasses += Math.floor(8 * separation);
        availableOptions[1].spectrumOptions.maxShiftPasses += Math.floor(4 * separation);
        availableOptions[2].spectrumOptions.smoothingPasses += Math.floor(12 * separation);
        availableOptions[2].spectrumOptions.maxShiftPasses += Math.floor(8 * separation);
        availableOptions[3].spectrumOptions.smoothingPasses += Math.floor(16 * separation);
        availableOptions[3].spectrumOptions.maxShiftPasses += Math.floor(12 * separation);
        availableOptions[4].spectrumOptions.smoothingPasses += Math.floor(20 * separation);
        availableOptions[4].spectrumOptions.maxShiftPasses += Math.floor(16 * separation);
        availableOptions[5].spectrumOptions.smoothingPasses += Math.floor(24 * separation);
        availableOptions[5].spectrumOptions.maxShiftPasses += Math.floor(20 * separation);
        availableOptions[6].spectrumOptions.smoothingPasses += Math.floor(28 * separation);
        availableOptions[6].spectrumOptions.maxShiftPasses += Math.floor(24 * separation);

        availableOptions[0].waveScale *= 0.25;
        availableOptions[1].waveScale *= 0.25 + 0.25 * separation;
        availableOptions[2].waveScale *= 0.25 + 0.50 * separation;
        availableOptions[3].waveScale *= 0.25 + 0.75 * separation;
        availableOptions[4].waveScale *= 0.25 + 1.0 * separation;
        availableOptions[5].waveScale *= 0.25 + 1.25 * separation;
        availableOptions[6].waveScale *= 0.25 + 1.50 * separation;
    } else if (layoutType === LAYOUT_TYPE.STACKED) {
        availableOptions[0].waveScale *= 0.25;
        availableOptions[1].waveScale *= 0.25 + 0.25 * separation;
        availableOptions[2].waveScale *= 0.25 + 0.50 * separation;
        availableOptions[3].waveScale *= 0.25 + 0.75 * separation;
        availableOptions[4].waveScale *= 0.25 + 1.0 * separation;
        availableOptions[5].waveScale *= 0.25 + 1.25 * separation;
        availableOptions[6].waveScale *= 0.25 + 1.50 * separation;
    } else if (layoutType === LAYOUT_TYPE.TARGET) {
        availableOptions[0].scale = 1;
        availableOptions[1].scale += 0.1 * separation;
        availableOptions[2].scale += 0.2 * separation;
        availableOptions[3].scale += 0.3 * separation;
        availableOptions[4].scale += 0.4 * separation;
        availableOptions[5].scale += 0.5 * separation;
        availableOptions[6].scale += 0.6 * separation;

        availableOptions[0].heightAdjust = 0;
        availableOptions[1].heightAdjust = 15 * separation;
        availableOptions[2].heightAdjust = 30 * separation;
        availableOptions[3].heightAdjust = 45 * separation;
        availableOptions[4].heightAdjust = 60 * separation;
        availableOptions[5].heightAdjust = 75 * separation;
        availableOptions[6].heightAdjust = 90 * separation;
    }
}

// `direction` (global Direction control) is threaded in because an inward direction needs the layer
// separation/ordering inverted — see configureAvailableOptions and initializeWaveCircles below.
export function createWaveCircleOptions(waveType, layoutType, num, delayed, separation, waveScale, barCount, reflectionType, smoothed, waveStyle, shape, direction, customSettings) {
    const computedBarCount = adjustBarCount(barCount, reflectionType, waveStyle);

    const { primarySpectrumOptions, baseWaveScale } = getSpectrumOptionsAndBaseWaveScale(
        waveType,
        shape,
        reflectionType,
        computedBarCount,
        smoothed
    );

    // 1. Initialization of the available options
    let availableOptions = createAvailableOptions(baseWaveScale, waveScale, primarySpectrumOptions, customSettings);

    // 2. Configure smoothingPasses, maxShiftPasses, waveScale, scale, heightAdjust
    configureAvailableOptions(availableOptions, layoutType, separation, direction);

    // 3. Initialization of the waveCircleOptions based on availableOptions
    let waveCircleOptions = initializeWaveCircles(num, layoutType, availableOptions, direction);

    // 4. Set up of Frame Delays
    setFrameDelays(num, delayed, waveCircleOptions);

    // 5. Apply custom settings to waveCircleOptions
    applyCustomSettings(customSettings, waveCircleOptions, shape);

    return waveCircleOptions;
}


function initializeWaveCircles(num, layoutType, availableOptions, direction) {
    let waveCircleOptions = [];

    if (num === 1) {
        waveCircleOptions.unshift(availableOptions[0]);
    }

    if (num === 2) {
        if (layoutType === LAYOUT_TYPE.WEBBED) {
            waveCircleOptions.unshift(availableOptions[0]);
            waveCircleOptions.unshift(availableOptions[4]);
        } else {
            waveCircleOptions.unshift(availableOptions[0]);
            waveCircleOptions.unshift(availableOptions[1]);
        }
    }

    if (num === 3) {
        if (layoutType === LAYOUT_TYPE.WEBBED) {
            waveCircleOptions.unshift(availableOptions[0]);
            waveCircleOptions.unshift(availableOptions[2]);
            waveCircleOptions.unshift(availableOptions[4]);
        } else {
            waveCircleOptions.unshift(availableOptions[0]);
            waveCircleOptions.unshift(availableOptions[1]);
            waveCircleOptions.unshift(availableOptions[2]);
        }
    }

    if (num === 4) {
        waveCircleOptions.unshift(availableOptions[0]);
        waveCircleOptions.unshift(availableOptions[1]);
        waveCircleOptions.unshift(availableOptions[2]);
        waveCircleOptions.unshift(availableOptions[3]);
    }

    if (num === 5) {
        waveCircleOptions.unshift(availableOptions[0]);
        waveCircleOptions.unshift(availableOptions[1]);
        waveCircleOptions.unshift(availableOptions[2]);
        waveCircleOptions.unshift(availableOptions[3]);
        waveCircleOptions.unshift(availableOptions[4]);
    }

    if (num === 6) {
        waveCircleOptions.unshift(availableOptions[0]);
        waveCircleOptions.unshift(availableOptions[1]);
        waveCircleOptions.unshift(availableOptions[2]);
        waveCircleOptions.unshift(availableOptions[3]);
        waveCircleOptions.unshift(availableOptions[4]);
        waveCircleOptions.unshift(availableOptions[5]);
    }

    if (num === 7) {
        waveCircleOptions.unshift(availableOptions[0]);
        waveCircleOptions.unshift(availableOptions[1]);
        waveCircleOptions.unshift(availableOptions[2]);
        waveCircleOptions.unshift(availableOptions[3]);
        waveCircleOptions.unshift(availableOptions[4]);
        waveCircleOptions.unshift(availableOptions[5]);
        waveCircleOptions.unshift(availableOptions[6]);
    }

    // The ladder above puts the smallest-amplitude option in front (last drawn), which is correct
    // for outward waves. For inward waves a larger amplitude carves a SMALLER inner disk, so the
    // front layer must instead be the largest-amplitude one; reversing the order achieves that and
    // keeps every layer visible. TARGET separates by ring radius (scale), not amplitude, so it
    // already reads correctly when inward and is intentionally excluded.
    if (direction === MOVEMENT_DIRECTION.INWARD && layoutType !== LAYOUT_TYPE.TARGET) {
        waveCircleOptions.reverse();
    }

    return waveCircleOptions;
}

function setFrameDelays(num, delayed, waveCircleOptions) {
    if (delayed) {
        if (num === 2) {
            waveCircleOptions[0].frameDelay = 3;
        } else if (num === 3) {
            waveCircleOptions[1].frameDelay = 2;
            waveCircleOptions[0].frameDelay = 4;
        } else if (num === 4) {
            waveCircleOptions[2].frameDelay = 1;
            waveCircleOptions[1].frameDelay = 2;
            waveCircleOptions[0].frameDelay = 3;
        } else if (num === 5) {
            waveCircleOptions[3].frameDelay = 1;
            waveCircleOptions[2].frameDelay = 2;
            waveCircleOptions[1].frameDelay = 3;
            waveCircleOptions[0].frameDelay = 4;
        } else if (num === 6) {
            waveCircleOptions[4].frameDelay = 1;
            waveCircleOptions[3].frameDelay = 2;
            waveCircleOptions[2].frameDelay = 3;
            waveCircleOptions[1].frameDelay = 4;
            waveCircleOptions[0].frameDelay = 5;
        } else if (num === 7) {
            waveCircleOptions[5].frameDelay = 1;
            waveCircleOptions[4].frameDelay = 2;
            waveCircleOptions[3].frameDelay = 3;
            waveCircleOptions[2].frameDelay = 4;
            waveCircleOptions[1].frameDelay = 5;
            waveCircleOptions[0].frameDelay = 6;
        }
    }
}

const adjustBarCount = (barCount, reflectionType, waveStyle) => {
    // Line draws a continuous polygon like Solid, so it uses the full point count too.
    if (waveStyle === WAVE_STYLE.SOLID || waveStyle === WAVE_STYLE.LINE)
        return barCount;

    switch (reflectionType) {
        case REFLECTION_TYPE.VERTICAL:
        case REFLECTION_TYPE.SLANTED:
        case REFLECTION_TYPE.ONE_SIDE:
        case REFLECTION_TYPE.COMBO:
            return Math.ceil(barCount / 2);
        case REFLECTION_TYPE.THREE_WAY:
            return Math.ceil(barCount / 3);
        case REFLECTION_TYPE.FOUR_WAY:
            return Math.ceil(barCount / 4);
        default:
            return barCount;
    }
}

export function getYouTubeCtaPosition(cornerPosition, previewWidth, previewHeight) {
    const contentWidth = Math.round(YOUTUBE_CTA_CONFIG.WIDTH * (previewHeight / PREVIEW_RESOLUTION.SEVEN_TWENTY));
    const contentHeight = Math.round(YOUTUBE_CTA_CONFIG.HEIGHT * (previewHeight / PREVIEW_RESOLUTION.SEVEN_TWENTY));

    if (cornerPosition === CORNER_POSITION.TOP_LEFT)
        return { x: 0, y: 0 };

    if (cornerPosition === CORNER_POSITION.TOP_RIGHT)
        return { x: previewWidth - contentWidth, y: 0 };

    if (cornerPosition === CORNER_POSITION.BOTTOM_LEFT)
        return { x: 0, y: previewHeight - contentHeight };

    if (cornerPosition === CORNER_POSITION.BOTTOM_RIGHT)
        return { x: previewWidth - contentWidth, y: previewHeight - contentHeight };

    if (cornerPosition === CORNER_POSITION.TOP)
        return { x: (previewWidth / 2) - (contentWidth / 2), y: 0 };

    if (cornerPosition === CORNER_POSITION.BOTTOM)
        return { x: (previewWidth / 2) - (contentWidth / 2), y: previewHeight - contentHeight };

    if (cornerPosition === CORNER_POSITION.RIGHT)
        return { x: previewWidth - contentWidth, y: (previewHeight / 2) - (contentHeight / 2) };

    if (cornerPosition === CORNER_POSITION.LEFT)
        return { x: 0, y: (previewHeight / 2) - (contentHeight / 2) };
}

export function getYouTubeCtaSize(previewWidth, previewHeight) {
    const contentWidth = Math.round(YOUTUBE_CTA_CONFIG.WIDTH * (previewHeight / PREVIEW_RESOLUTION.SEVEN_TWENTY));
    const contentHeight = Math.round(YOUTUBE_CTA_CONFIG.HEIGHT * (previewHeight / PREVIEW_RESOLUTION.SEVEN_TWENTY));

    return {
        width: contentWidth,
        height: contentHeight
    };
}

export function generateYouTubeCtaFrames() {
    const frames = [];

    for (let i = YOUTUBE_CTA_CONFIG.FIRST_FRAME_NUMBER; i <= YOUTUBE_CTA_CONFIG.LAST_FRAME_NUMBER; i += 2) {
        let frameNumber;

        if (i < 10)
            frameNumber = `0000${i}`;
        else if (i < 100)
            frameNumber = `000${i}`;
        else if (i < 1000)
            frameNumber = `00${i}`;

        const frameUrl = YOUTUBE_CTA_CONFIG.FRAMES_LOCATION.replace(YOUTUBE_CTA_CONFIG.FRAME_NUMBER_KEY, frameNumber);
        frames.push(frameUrl);
    }

    return frames;
}

export function ninetyDegreeTurnsToRadians(turns) {
    return turns * 1.5708;
}

// DropShadowFilter measures its own filter region as `distance + blur * 2`, which is how far a Kawase
// blur reaches only at the library's default quality of three passes. Image element shadows run at a
// higher quality so the passes stop showing as bands, and every extra pass widens the reach, so the
// region has to be measured from the passes themselves. Left to the filter, a wide shadow is cut off
// at a rectangle and reads as a hard square instead of fading out. The export renderer measures it
// the same way in helpers/image-element-helpers.js.
export function calculateShadowFilterPadding(blurKernels, offsetDistance) {
    const kernelSpread = blurKernels.reduce((total, kernel) => total + kernel + 0.5, 0);

    return Math.ceil(Math.abs(offsetDistance) + kernelSpread);
}